import { ArrowCircleDown, ArrowCircleUp } from "@mui/icons-material";
import {
  AddCircleOutline as AddCircleOutlineIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  EditNote as EditNoteIcon
} from "@mui/icons-material";
import {
  Box,
  Collapse,
  FormControl,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from "@mui/material";
import { useRef, useState } from "react";
import { TPizza, TPizzaMenu } from "../types/AllTypes";
import { MyButton } from "./MyButton";
// import { EditNote } from "@mui/icons-material";

type PizzaProps = {
  pizza: TPizza;
  addPizza?: (pizza: TPizza) => void;
  removePizza?: () => void;
  changeNote?: (note: string) => void;
  count?: number;
  customName?: string;
  customPrice?: number;
  editPizza?: (pizza: TPizza) => void;
  textcolor?: string;
  showPosition?: boolean;
  upPosition?: (pizza: TPizza) => void;
  downPosition?: (pizza: TPizza) => void;
  elevation?: number;
};

export const Pizza = ({
  addPizza,
  removePizza,
  changeNote,
  pizza,
  count,
  customName,
  customPrice,
  editPizza,
  textcolor,
  showPosition,
  upPosition,
  downPosition,
  elevation = 1
}: PizzaProps) => {
  const { name: aname, price, ingredients } = pizza;
  const name = customName ?? aname;
  const [noteVisible, setNoteVisible] = useState(!!pizza.note);
  const [note, setNote] = useState(pizza.note ?? "");
  textcolor = textcolor ?? "text.primary";

  const textFieldRef = useRef<HTMLDivElement>(null);
  let countX = "";
  if (count) {
    countX = count + "x ";
  }

  return (
    <Box padding={3} paddingY={1.5} width={"350px"} sx={{ margin: "0 auto" }}>
      <Paper elevation={elevation}>
        <Box padding={2}>
          <Grid container>
            <Grid item xs={9}>
              <Typography variant="h5" color={textcolor}>
                {countX}
                {name}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Box display={"flex"} justifyContent={"end"}>
                {customPrice ? (
                  <Box textAlign={"end"}>
                    <Typography color={textcolor}>
                      {countX}
                      {customPrice.toFixed(2)}€
                    </Typography>
                    <Typography
                      color={textcolor}
                      variant="caption"
                      sx={{ textDecoration: "line-through", opacity: 0.7 }}
                    >
                      {countX}
                      {price.toFixed(2)}€
                    </Typography>
                  </Box>
                ) : (
                  <Typography color={textcolor}>
                    {countX}
                    {price.toFixed(2)}€
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2" color={textcolor}>
                {ingredients}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Box display={"flex"} justifyContent={"end"}>
                <Typography textAlign={"end"}>
                  {editPizza && (
                    <IconButton
                      onClick={() => {
                        editPizza(pizza);
                      }}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                  {changeNote && !noteVisible && (
                    <IconButton
                      onClick={() => {
                        setNoteVisible(!noteVisible);
                      }}
                      color="primary"
                    >
                      <EditNoteIcon />
                    </IconButton>
                  )}
                  {addPizza && (
                    <IconButton
                      onClick={() => {
                        addPizza(pizza);
                      }}
                      color="primary"
                    >
                      <AddCircleOutlineIcon />
                    </IconButton>
                  )}
                  {removePizza && (
                    <IconButton
                      onClick={() => {
                        removePizza();
                      }}
                      color="primary"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                  {/* </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box display={"flex"} justifyContent={"end"}>
                <Typography> */}
                  {downPosition && (
                    <IconButton
                      onClick={() => {
                        downPosition(pizza);
                      }}
                      color="primary"
                    >
                      <ArrowCircleDown />
                    </IconButton>
                  )}
                  {showPosition && <>{pizza.position}</>}
                  {upPosition && (
                    <IconButton
                      onClick={() => {
                        upPosition(pizza);
                      }}
                      color="primary"
                    >
                      <ArrowCircleUp />
                    </IconButton>
                  )}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Collapse unmountOnExit in={noteVisible}>
            {
              <Box marginTop={1}>
                <TextField
                  ref={textFieldRef}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      textFieldRef.current?.blur();
                    }
                  }}
                  disabled={!changeNote}
                  onBlur={() => {
                    if (note.length == 0) {
                      setNoteVisible(false);
                    }
                  }}
                  value={note}
                  onChange={e => {
                    setNote(e.target.value);
                    changeNote && changeNote(e.target.value);
                  }}
                  autoFocus
                  variant="standard"
                  label="Poznamky"
                  fullWidth
                />
              </Box>
            }
          </Collapse>
        </Box>
      </Paper>
    </Box>
  );
};

export const PizzaForm = ({
  pizza,
  onChange,
  onSave,
  getPizzaMenuWithId,
  pizzaMenus
}: {
  pizza: TPizza;
  onChange: <T extends keyof TPizza>(
    fieldChanged: T,
    newValue: TPizza[T]
  ) => void;
  onSave: () => void;
  getPizzaMenuWithId: (id: string | number) => TPizzaMenu | undefined;
  pizzaMenus: TPizzaMenu[];
}) => {
  return (
    <>
      <Box padding={3} marginBottom={5}>
        <FormGroup>
          <TextField
            sx={{ margin: 1 }}
            value={pizza.name}
            onChange={e => onChange("name", e.target.value)}
            label="Name"
            variant="standard"
            fullWidth
          />
          <TextField
            sx={{ margin: 1 }}
            value={pizza.ingredients}
            onChange={e => onChange("ingredients", e.target.value)}
            label="Ingredients"
            variant="standard"
            fullWidth
          />
          <TextField
            sx={{ margin: 1 }}
            value={pizza.price}
            onChange={e => onChange("price", parseFloat(e.target.value) || 0)}
            label="Price"
            variant="standard"
            fullWidth
            type="number"
          />
          <TextField
            sx={{ margin: 1 }}
            value={pizza.position}
            onChange={e => onChange("position", parseInt(e.target.value) || 0)}
            label="Position"
            variant="standard"
            fullWidth
            type="number"
          />
          <FormControl fullWidth sx={{ margin: 1 }}>
            <InputLabel id="select-pizza-menu-label">Pizza menu</InputLabel>
            <Select
              labelId="select-pizza-menu-label"
              id="select-pizza-menu"
              value={pizza.pizzaMenu.id}
              label="Pizza menu"
              onChange={e => {
                const menu = getPizzaMenuWithId(e.target.value);
                if (menu) onChange("pizzaMenu", menu);
              }}
            >
              {pizzaMenus.map(menu => {
                return <MenuItem value={menu.id}>{menu.name}</MenuItem>;
              })}
            </Select>
          </FormControl>
        </FormGroup>
      </Box>
      <Box position={"fixed"} bottom={20} width={"100%"} paddingX={2}>
        <MyButton fullWidth onClick={onSave}>
          <Typography
            sx={{
              fontWeight: 700
            }}
            variant="h6"
          >
            ULOZIT
          </Typography>
        </MyButton>
      </Box>
    </>
  );
};

export const PizzaMenuForm = ({
  pizzaMenu,
  onChange,
  onSave
}: {
  pizzaMenu: TPizzaMenu;
  onChange: <T extends keyof TPizzaMenu>(
    fieldChanged: T,
    newValue: TPizzaMenu[T]
  ) => void;
  onSave: () => void;
}) => {
  const [errors, setErrors] = useState({
    name: ""
  });
  const submit = () => {
    if (pizzaMenu.name.length > 0) {
      onSave();
    } else {
      setErrors({ ...errors, name: "Zadaj nazov" });
    }
  };
  return (
    <>
      <Box padding={3} marginBottom={5}>
        <FormGroup>
          <TextField
            sx={{ margin: 1 }}
            value={pizzaMenu.name}
            onChange={e => {
              onChange("name", e.target.value);
              setErrors({ ...errors, name: "" });
            }}
            label="Name"
            variant="standard"
            error={errors.name.length > 0}
            helperText={errors.name}
            required
            fullWidth
          />
        </FormGroup>
      </Box>
      <Box position={"fixed"} bottom={20} width={"100%"} paddingX={2}>
        <MyButton fullWidth onClick={submit}>
          <Typography
            sx={{
              fontWeight: 700
            }}
            variant="h6"
          >
            ULOZIT
          </Typography>
        </MyButton>
      </Box>
    </>
  );
};
